import React, { useState } from 'react';
import axios from 'axios';
import Drawer, { DrawerSection, Field, inputClass } from './Drawer';
import ErrorAlert from './ErrorAlert';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import { IconPlus, IconEdit } from '../icons';

// Add mode takes many store URLs at once (one per line); edit mode changes one app.
const DecoyAppModal = ({ decoyApp, onClose }) => {
  const isEdit = !!decoyApp;
  const [urls, setUrls] = useState('');
  const [name, setName] = useState(decoyApp?.name || '');
  const [storeUrl, setStoreUrl] = useState(decoyApp?.storeUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const urlList = urls.split('\n').map((u) => u.trim()).filter(Boolean);
  const canSubmit = isEdit ? !!storeUrl.trim() : urlList.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const toValidate = isEdit ? [storeUrl.trim()] : urlList;
    const invalid = toValidate.find((u) => {
      try {
        new URL(u);
        return false;
      } catch {
        return true;
      }
    });
    if (invalid) {
      setError(`Invalid URL: ${invalid}`);
      return;
    }

    setLoading(true);
    try {
      const response = isEdit
        ? await axios.put(`/api/decoy-apps/${decoyApp._id}`, { name, storeUrl: storeUrl.trim() })
        : await axios.post('/api/decoy-apps', { storeUrls: urlList });
      if (response.data.success) {
        onClose(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save decoy app');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <SecondaryButton onClick={() => onClose(false)} disabled={loading} className="w-[160px] h-10">
        Cancel
      </SecondaryButton>
      <PrimaryButton
        type="submit"
        form="decoy-app-form"
        loading={loading}
        disabled={!canSubmit}
        className="w-[160px] h-10"
      >
        {isEdit ? 'Save' : urlList.length > 1 ? `Add ${urlList.length} apps` : 'Add app'}
      </PrimaryButton>
    </>
  );

  return (
    <Drawer
      isOpen
      onClose={() => onClose(false)}
      closeDisabled={loading}
      title={isEdit ? 'Edit Decoy App' : 'Add Decoy Apps'}
      icon={isEdit ? <IconEdit size={18} /> : <IconPlus size={18} />}
      footer={footer}
    >
      <form id="decoy-app-form" onSubmit={handleSubmit}>
        <div className="flex flex-col py-5 gap-5">
          <DrawerSection title="App Info">
            <ErrorAlert message={error} onDismiss={() => setError('')} />

            {isEdit ? (
              <>
                <Field label="App Name">
                  <input
                    type="text"
                    id="decoyName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={loading}
                    className={inputClass}
                  />
                </Field>
                <Field label="Store URL *">
                  <input
                    type="url"
                    id="decoyStoreUrl"
                    value={storeUrl}
                    onChange={(e) => setStoreUrl(e.target.value)}
                    disabled={loading}
                    className={inputClass}
                    required
                  />
                </Field>
              </>
            ) : (
              <Field
                label="Store URLs *"
                hint="One URL per line. App Store names and icons are filled in automatically; duplicates are skipped"
              >
                <textarea
                  id="decoyStoreUrls"
                  value={urls}
                  onChange={(e) => { setUrls(e.target.value); setError(''); }}
                  disabled={loading}
                  className={`${inputClass} resize-y font-mono text-xs`}
                  placeholder={'https://apps.apple.com/us/app/.../id123456789\nhttps://play.google.com/store/apps/details?id=...'}
                  rows="10"
                />
              </Field>
            )}
          </DrawerSection>
        </div>
      </form>
    </Drawer>
  );
};

export default DecoyAppModal;
